import { Configuration as AuthzConfiguration } from '@c4-links-gcs/gcs-authz-sdk';
import { Configuration as BffConfiguration } from '@carrefour-gcs/bff-sdk';
import type { AxiosError, AxiosInstance } from 'axios';
import axios from 'axios';
import type { ApiType } from './types/api.type';

/**
 * @abstract
 * @class ApiConfiguration
 * Abstract class that provides Axios instance and API configuration for interacting with Authz service API.
 *    This class sets up the base URL, headers, and credentials for Axios requests, and includes error handling through interceptors.
 */
export abstract class ApiConfiguration {
  public axiosInstance!: AxiosInstance;
  public bffConfiguration!: BffConfiguration;
  public authzConfiguration!: AuthzConfiguration;

  public readonly apiType!: ApiType;

  constructor(api: ApiType, baseUrl: string) {
    this.apiType = api;

    this.setAxiosInstance(baseUrl);
    this.setConfiguration(baseUrl);
    this.setInterceptors();
  }

  /**
   * Set the baseURL to axios instance.
   *  If you need to change Axios configuration, you can do it here
   */
  private setAxiosInstance(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });
  }

  /**
   * Set the base path to configuration
   *  If you need to add token or other headers, you can do it here
   */
  private setConfiguration(basePath: string) {
    switch (this.apiType) {
      case 'AUTHZ':
        this.authzConfiguration = new AuthzConfiguration({ basePath });
        break;
      case 'BFF':
        this.bffConfiguration = new BffConfiguration({ basePath });
        break;
      case 'PROJECTS':
        break;

      default:
        throw new Error(`Unsupported API type: ${this.apiType}`);
    }
  }

  /**
   * Set the interceptor of response of request.
   *  If you need to handle errors or responses globally, you can do it here.
   */
  private setInterceptors(): void {
    switch (this.apiType) {
      case 'AUTHZ':
      case 'BFF':
      case 'PROJECTS':
        this.setErrorMappingInterceptors();
        break;

      default:
        throw new Error(`Unsupported API type: ${this.apiType}`);
    }
  }

  /**
   * Set the interceptor of response of request for Authz and BFF APIs.
   *  If you need to handle errors or responses globally, you can do it here
   */
  private setErrorMappingInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },

      (error: AxiosError) => {
        const errorMessage = error.message || 'An unknown error occurred';
        const mappedError = new Error(errorMessage);
        mappedError.stack = error.stack;

        return Promise.reject(mappedError);
      }
    );
  }
}
